import React, { useState, useEffect } from 'react';
import DataDashboard from '../../../common/DataDashboard';
import { AdminUser } from '../../../../types/admin-v2';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';

interface InboxDashboardProps {
  user: AdminUser;
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
  showDoneOnly?: boolean;
}

interface InboxItem {
  _id: string;
  mailbox_id: string;
  thread_id: string;
  request_id: string;
  customer: string;
  supplier: string;
  rfq_name: string;
  rfq_open: string;
  request_date: string;
  is_replied: boolean;
  last_activity: string;
  status: string;
  is_done?: boolean;
  done_at?: string;
  last_message_direction?: string;
}

const InboxDashboard: React.FC<InboxDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout,
  showDoneOnly = false
}) => {
  const { token } = useAuth();
  const [inboxData, setInboxData] = useState<InboxItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Load inbox data from API
  const loadInbox = async () => {
    if (!token) return;

    setIsLoading(true);
    setError(null);

    try {
      const endpoint = showDoneOnly
        ? `${API_ENDPOINTS.INBOX}?is_done=true`
        : `${API_ENDPOINTS.INBOX}?is_done=false`;

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setInboxData(result.data || []);
        } else {
          setError(result.message || 'Failed to load inbox');
        }
      } else {
        setError('Failed to load inbox data');
      }
    } catch (err: any) {
      console.error('Error loading inbox:', err);
      setError(err.message || 'Failed to load inbox');
    } finally {
      setIsLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    loadInbox();
  }, [token, showDoneOnly]);

  // Handle data change
  const handleDataChange = (newData: InboxItem[]) => {
    setInboxData(newData);
  };

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectedItemsCount, setSelectedItemsCount] = useState<number>(0);

  // Handle selection change
  const handleSelectionChange = (selectedIds: string[]) => {
    setSelectedItems(selectedIds);
    setSelectedItemsCount(selectedIds.length);
  };

  // Handle Open button click - navigate to RFQ communication
  const handleOpen = () => {
    if (selectedItems.length > 0) {
      const selectedId = selectedItems[0];
      const selectedItem = transformedInboxData.find(item => {
        const itemId = item._id?.toString() || item.id?.toString() || item.mailbox_id?.toString() || '';
        return itemId === selectedId;
      });
      
      if (selectedItem && selectedItem.request_id) {
        // Navigate to RFQ communication page
        const requestId = selectedItem.request_id.toString();
        onNavigate(`/dashboard/rfqs/open/${requestId}/communication`);
      }
    }
  };

  // Render RFQ Open status
  const renderRFQOpenCell = (value: string) => {
    return <span>{value}</span>;
  };

  // Render Is Replied
  const renderIsRepliedCell = (value: boolean) => {
    return <span>{value ? 'True' : 'False'}</span>;
  };

  const normalizeDateValue = (value?: string) => {
    if (!value) return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/[Zz]$/.test(trimmed) || /[+\-]\d{2}:\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
      const withZ = trimmed.endsWith('Z') ? trimmed : `${trimmed}Z`;
      return withZ.replace(/(\.\d{3})\d+Z$/, '$1Z');
    }
    return trimmed;
  };

  const parseDateValue = (value?: string): Date | null => {
    const normalized = normalizeDateValue(value);
    if (!normalized) return null;
    const timestamp = Date.parse(normalized);
    if (Number.isNaN(timestamp)) return null;
    return new Date(timestamp);
  };

  const formatDateTime = (value?: string) => {
    const parsed = parseDateValue(value);
    if (!parsed) return 'N/A';
    try {
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZoneName: 'short'
      }).format(parsed);
    } catch {
      return parsed.toLocaleString();
    }
  };

  const renderLastMessageBy = (direction?: string) => {
    if (!direction) return 'Unknown';
    if (direction === 'sent') return 'Us';
    if (direction === 'received') return 'Supplier';
    return direction;
  };

  const renderTimeSince = (value?: string) => {
    const parsed = parseDateValue(value);
    if (!parsed) return 'N/A';
    const diffMs = Date.now() - parsed.getTime();
    if (diffMs < 0) {
      return 'Just now';
    }
    const diffMinutes = Math.floor(diffMs / 60000);
    const days = Math.floor(diffMinutes / 1440);
    const hours = Math.floor((diffMinutes % 1440) / 60);
    const minutes = diffMinutes % 60;
    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);
    return parts.join(' ');
  };

  // Transform data to ensure _id is available for DataDashboard
  const transformedInboxData = inboxData.map(item => ({
    ...item,
    id: item._id || item.mailbox_id, // Ensure 'id' field exists for DataDashboard
    _id: item._id || item.mailbox_id, // Ensure '_id' field exists
    _isStale: (() => {
      const parsed = parseDateValue(item.last_activity);
      if (!parsed) return false;
      const diffMs = Date.now() - parsed.getTime();
      return diffMs >= 24 * 60 * 60 * 1000;
    })()
  }));

  // Column definitions for inbox
  const baseColumns = [
    {
      key: '_id',
      title: 'ID',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '100px',
      render: (value: any) => {
        // Show short ID (first 8 chars)
        const idStr = value?.toString() || '';
        return idStr.length > 8 ? idStr.substring(0, 8) + '...' : idStr;
      }
    },
    {
      key: 'customer',
      title: 'Customer',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '200px'
    },
    {
      key: 'supplier',
      title: 'Supplier',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '200px'
    },
    {
      key: 'last_message_direction',
      title: 'Last Message By',
      type: 'text' as const,
      sortable: false,
      editable: false,
      width: '140px',
      render: renderLastMessageBy
    },
    {
      key: 'rfq_name',
      title: 'RFQ Name',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '250px'
    },
    {
      key: 'rfq_open',
      title: 'RFQ Open',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '120px',
      render: renderRFQOpenCell
    },
    {
      key: 'request_date',
      title: 'Request Date',
      type: 'date' as const,
      sortable: true,
      editable: false,
      width: '150px',
      render: (value: string) => {
        return formatDateTime(value);
      }
    },
    {
      key: 'last_activity',
      title: 'Time Since Last Message',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '160px',
      render: renderTimeSince
    },
    {
      key: 'is_replied',
      title: 'Is Replied',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '120px',
      render: renderIsRepliedCell
    }
  ];

  const inboxColumns = [...baseColumns];

  if (showDoneOnly) {
    inboxColumns.push({
      key: 'done_at',
      title: 'Finished At',
      type: 'date' as const,
      sortable: true,
      editable: false,
      width: '160px',
      render: (value: string) => formatDateTime(value)
    });
  }

  const viewTitle = showDoneOnly ? 'Old Mails' : 'Inbox';
  const viewDescription = showDoneOnly
    ? 'Finished email conversations with suppliers'
    : 'Active email conversations with suppliers';

  return (
    <DataDashboard
      title={viewTitle}
      collection="mailboxes"
      data={transformedInboxData}
      columns={inboxColumns}
      isLoading={isLoading}
      error={error}
      user={user}
      currentRoute={currentRoute}
      onNavigate={onNavigate}
      onLogout={onLogout}
      onDataChange={handleDataChange}
      showAddButton={false}
      showSearch={true}
      showSelection={true}
      showEditButton={false}
      showDeleteButton={false}
      layoutType="admin"
      onOpen={handleOpen}
      selectedItems={selectedItems}
      selectedItemsCount={selectedItemsCount}
      onSelectionChange={handleSelectionChange}
      pageDescription={viewDescription}
    />
  );
};

export default InboxDashboard;
